import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { useEmployeeStore } from '../stores/employeeStore';
import { useAdminStore } from '../stores/adminStore';

export function AuthInitializer({ children }: { children: React.ReactNode }) {
    const setAuth = useAuthStore(state => state.setAuth);
    const initializeEmployeeStore = useEmployeeStore(state => state.initialize);
    const initializeAdminStore = useAdminStore(state => state.fetchAdminData);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const session = await authService.getSession();
                if (session) {
                    try {
                        const profile = await authService.getProfile();
                        setAuth({
                            user: profile,
                            session
                        });
                        // PostHog removed - no user identification
                        // Initialize appropriate store based on role
                        if (profile.role === 'admin') {
                            await initializeAdminStore();
                        } else {
                            await initializeEmployeeStore();
                        }
                    } catch (profileError) {
                        console.error('Error fetching profile:', profileError);
                        // If profile fetch fails, use basic user info from session
                        const user = await authService.getUser();
                        if (user) {
                            const role = user.user_metadata?.role || 'employee';
                            const userData = {
                                id: user.id,
                                email: user.email || '',
                                role,
                                name: user.user_metadata?.name || '',
                                org_id: user.user_metadata?.org_id || ''
                            };
                            setAuth({
                                user: userData,
                                session
                            });
                            // PostHog removed - no user identification
                            // Initialize appropriate store based on role
                            if (role === 'admin') {
                                await initializeAdminStore();
                            } else {
                                await initializeEmployeeStore();
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
            } finally {
                setIsInitialized(true);
            }
        };

        initializeAuth();
    }, [setAuth, initializeEmployeeStore, initializeAdminStore]);

    if (!isInitialized) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-600"></div>
            </div>
        );
    }

    return <>{children}</>;
} 